import React, { useCallback, useEffect, useRef, useState } from 'react'

import { reactular } from '@vendhq/reactular'
import { debounce } from 'lodash'
import { vn } from 'vend-number'
import { LineItem } from '../../../models/line-item'
import { displayRound } from '../../../filters/display-round'
import {
  useRetailerSettingsManager,
  useSaleManager,
  useUserHasLineItemDiscountRule,
  useVdFeatureManager,
  useVdTracking,
} from '../../../ng-compat/hooks'
import { $rootScope } from '../../../ng-compat/imports'
import { ERRORS, FEATURE_ENABLE_CONTROL_ON_DISCOUNT } from '../../../constants'

import { LineItemInput } from './LineItemInput'
import { useLineItemDiscountRule } from './useLineItemDiscountRule'

const MAX_PRICE = 10000000000 // Ten billion

interface Props {
  lineItem: LineItem
  editAllowedForConfirmed?: boolean
  onError: (errors: string | null) => void
  error: string | null
  hasUserSwitched: () => boolean
  readonly?: boolean
}

export const LineItemPrice = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      lineItem,
      editAllowedForConfirmed = false,
      onError,
      error: initialError,
      hasUserSwitched,
      readonly = false,
    },
    ref
  ) => {
    const vdTracking = useVdTracking()
    const saleManager = useSaleManager()
    const discountPermission = useUserHasLineItemDiscountRule()

    const [error, setError] = useState<string | null>(initialError)
    const [value, setValue] = useState<string | number>(lineItem.displayPrice)
    const [initialPaddingLeft, setInitialPaddingLeft] = useState<number | null>(
      null
    )
    const currencyRef = useRef<HTMLSpanElement>(null)
    const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null)

    const retailerCurrencySymbol = useRetailerSettingsManager().get().currency
      .symbol

    const isReadOnlyField =
      readonly ||
      !discountPermission ||
      (lineItem.confirmed && !editAllowedForConfirmed) ||
      lineItem.isReturn
    const [disabled, setDisabled] = useState<boolean>(isReadOnlyField)

    useEffect(() => {
      if (error !== ERRORS.INVALID_DISCOUNT) {
        setValue(lineItem.displayPrice)
      }
    }, [lineItem, error])

    const vdFeatureManager = useVdFeatureManager()
    const hasControlOnDiscountEnabled = vdFeatureManager.get(
      FEATURE_ENABLE_CONTROL_ON_DISCOUNT
    )

    const {
      isPriceDiscountValid,
      lineItemDiscountRule,
    } = useLineItemDiscountRule(lineItem)

    const setErrorState = (error: string | null) => {
      setError(error)
      onError(error)
    }

    const setDisabledState = (disabled: boolean) => {
      if (!isReadOnlyField || disabled) {
        setDisabled(disabled)
      }
    }

    // validate the discount set based on user line item discount rules
    const validate = useCallback(
      (config: { value: string | number; ignoreAdjusted?: boolean }) => {
        if (
          (lineItem.adjusted || config.ignoreAdjusted) &&
          hasControlOnDiscountEnabled &&
          !isPriceDiscountValid(config.value)
        ) {
          return ERRORS.INVALID_DISCOUNT
        }

        if (vn(config.value).gt(MAX_PRICE)) {
          return ERRORS.INVALID_MAX
        }

        return null
      },
      [hasControlOnDiscountEnabled, isPriceDiscountValid, lineItem.adjusted]
    )

    useEffect(() => {
      if (
        !(inputEl instanceof HTMLInputElement) ||
        !(currencyRef.current instanceof HTMLSpanElement)
      ) {
        return
      }

      const currentPaddingLeft = parseInt(
        getComputedStyle(inputEl).paddingLeft,
        10
      )

      if (initialPaddingLeft === null) {
        setInitialPaddingLeft(currentPaddingLeft)
        return
      }

      const currencyWidth = currencyRef.current.offsetWidth

      const newPaddingLeft = initialPaddingLeft + currencyWidth

      inputEl.style.paddingLeft = `${newPaddingLeft}px`
    }, [initialPaddingLeft, inputEl, retailerCurrencySymbol])

    const handleRef = (node: HTMLInputElement | null) => {
      setInputEl(node)
      if (typeof ref === 'function') {
        ref(node)
      } else if (ref) {
        ref.current = node
      }
    }

    // re-render whenever the error changes on the angular side
    useEffect(() => {
      setError(initialError)
    }, [initialError])

    useEffect(() => {
      const error = validate({ value })
      // Only disable if the discount is invalid
      setDisabledState(error === ERRORS.INVALID_DISCOUNT)
      // on the first render, set field disabled if current user is not allowed to have/modify the current discount value
      // since we do not persist line items with invalid discount this would've been set by a user with higher privileges
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      // Angular periodically recreates the component and calls loggedInUserChange
      // this useEffect should only run if the user really switched
      if (hasUserSwitched()) {
        const discountValue =
          error === ERRORS.INVALID_DISCOUNT ? lineItem.displayPrice : value

        setValue(discountValue)
        const newLoggedInUserError = validate({
          value: discountValue,
        })
        const isInvalidDiscountError =
          newLoggedInUserError === ERRORS.INVALID_DISCOUNT

        setDisabledState(isReadOnlyField || isInvalidDiscountError)

        if (error === ERRORS.INVALID_DISCOUNT) {
          setErrorState(null)
        }
      }
      // when userRule changes (when switching users)
      // set the previous valid discount if the current value was invalid before switching users
      // and/or make the field read-only + clear errors if the new logged in user
      // is not allowed to have/modify the current discount value
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lineItemDiscountRule, hasUserSwitched()])

    // TODO: [LR-10986] move this inside onChange
    const sendUbe = useRef(
      debounce((inputValue: string, err: string | null) => {
        const eventName = err
          ? 'sale.line-item.price-changed.not_allowed_error'
          : 'sale.line-item.price-changed'
        vdTracking.trackUserEvent({
          eventName,
          metadata: {
            valueSet: inputValue,
          },
        })
      }, 300)
    )

    // TODO: [LR-10986] add debounce
    const onChange = (inputValue: string) => {
      const error = validate({
        value: inputValue,
        ignoreAdjusted: true,
      })
      setErrorState(error)
      setValue(inputValue)

      if (error !== ERRORS.INVALID_DISCOUNT) {
        saleManager.updateLineItemDisplayPrice({
          lineItem: {
            ...lineItem,
            displayPrice: inputValue,
          },
        })
      }

      sendUbe.current(inputValue, error)
    }

    const onBlur = useCallback(
      (_: string) => {
        $rootScope.$emit('sale:update-line-item', lineItem)
      },
      [lineItem]
    )

    return (
      <div>
        <div className="vd-field li-cell-actions-field">
          <div className="vd-label">Price</div>
          <div className="vd-value li-cell-actions-value">
            {disabled ? (
              <div>{`${retailerCurrencySymbol}${displayRound(
                lineItem.displayPrice
              )}`}</div>
            ) : (
              <>
                <LineItemInput
                  className="li-input--price"
                  allowedPrefix="$"
                  decimalPlaces={2}
                  onError={setErrorState}
                  error={error}
                  onValueChange={onChange}
                  onFocusChange={onBlur}
                  ref={handleRef}
                  value={value}
                  max={MAX_PRICE}
                />
                <span
                  ref={currencyRef}
                  className="vd-input-icon vd-input-icon--left vd-input-symbol"
                >
                  {retailerCurrencySymbol}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }
)
LineItemPrice.displayName = 'LineItemPrice'

export const LineItemPriceComponent = reactular(
  LineItemPrice,
  [
    'lineItem',
    'editAllowedForConfirmed',
    'onError',
    'ref',
    'error',
    'hasUserSwitched',
  ],
  'reactWrapper'
)
